import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { typedFetch } from '../../typedFetch';
import './Schedule.scss';
const scheduleIdFromURL = Number(window.location.pathname.match(/^\/schedules\/(\d+)/)?.[1]);
export const Schedule = ({ appendToErrorLog, loggedInUser, }) => {
    const [loading, setLoading] = useState(true);
    const [schedule, setSchedule] = useState(null);
    const [department, setDepartment] = useState('');
    const [departments, setDepartments] = useState([]);
    const [ticketName, setTicketName] = useState('');
    const [ticketDescription, setTicketDescription] = useState('');
    const [firstTicketDate, setFirstTicketDate] = useState('');
    const [frequencyInDays, setFrequencyInDays] = useState(365);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    useEffect(() => {
        if (!loggedInUser) {
            appendToErrorLog('Only logged-in users may view schedules');
            setLoading(false);
            return;
        }
        if (!scheduleIdFromURL) {
            // User is on new schedule page (probably)
            typedFetch('departments', { body: { action: 'get' } })
                .then((result) => {
                if (result.success) {
                    const visibleDepartments = loggedInUser.supervisor ? result.departments : loggedInUser.departments;
                    setDepartments(visibleDepartments);
                    setDepartment(visibleDepartments[0]);
                }
                else {
                    appendToErrorLog(`Failed to retrieve departments: ${result.reason}`);
                }
            })
                .catch((error) => {
                appendToErrorLog(`Unexpected error while retrieving departments: ${String(error['message'])}`);
            })
                .finally(() => {
                setLoading(false);
            });
            return;
        }
        typedFetch('schedule', {
            body: {
                action: 'get',
                scheduleId: scheduleIdFromURL,
            },
        })
            .then((result) => {
            if (result.success) {
                setSchedule(result.schedule);
                setTicketName(result.schedule.ticketName);
                setTicketDescription(result.schedule.ticketDescription);
                setFirstTicketDate(result.schedule.firstTicketDate);
                setFrequencyInDays(result.schedule.frequencyInDays);
            }
            else {
                appendToErrorLog(`Failed to retrieve schedule: ${result.reason}`);
            }
        })
            .catch((error) => {
            appendToErrorLog(`Unexpected error while retrieving schedule: ${String(error['message'])}`);
        })
            .finally(() => {
            setLoading(false);
        });
    }, [loggedInUser, appendToErrorLog]);
    const savable = (schedule && (schedule.ticketName !== ticketName
        || schedule.ticketDescription !== ticketDescription
        || schedule.firstTicketDate !== firstTicketDate
        || schedule.frequencyInDays !== frequencyInDays))
        ?? (!schedule && !scheduleIdFromURL && (department
            && ticketName
            && ticketDescription
            && firstTicketDate
            && frequencyInDays));
    const saveSchedule = () => {
        if (!loggedInUser) {
            return;
        }
        if (!savable) {
            appendToErrorLog('No changes to save');
            return;
        }
        const action = scheduleIdFromURL ? 'update' : 'create';
        const body = {
            action,
            department,
            ticketName,
            ticketDescription,
            firstTicketDate,
            frequencyInDays,
            ...(scheduleIdFromURL && { scheduleId: scheduleIdFromURL }),
        };
        typedFetch('schedule', { body })
            .then((result) => {
            if (result.success) {
                if (scheduleIdFromURL) {
                    // Save button will disappear
                    setSchedule(result.schedule);
                }
                else {
                    window.location.href = `/schedules/${result.schedule.id}`;
                }
            }
            else {
                appendToErrorLog(`Failed to ${action} schedule: ${result.reason}`);
            }
        })
            .catch((error) => {
            appendToErrorLog(`Unexpected error while ${action}ing schedule: ${String(error['message'])}`);
        });
    };
    const deleteSchedule = () => {
        if (!loggedInUser || !scheduleIdFromURL) {
            return;
        }
        typedFetch('schedule', {
            body: {
                action: 'delete',
                scheduleId: scheduleIdFromURL,
            },
        })
            .then((result) => {
            if (result.success) {
                window.location.href = '/schedules';
            }
            else {
                appendToErrorLog(`Failed to delete schedule: ${result.reason}`);
            }
        })
            .catch((error) => {
            appendToErrorLog(`Unexpected error while deleting schedule: ${String(error['message'])}`);
        });
    };
    if (loading) {
        return 'Processing...';
    }
    if (!loggedInUser) {
        return 'No schedule to display';
    }
    return (_jsxs("main", { className: "schedule", children: [_jsx("h2", { children: scheduleIdFromURL ? `Schedule #${scheduleIdFromURL}` : 'Create new schedule' }), !Number.isNaN(scheduleIdFromURL) && (_jsxs("div", { className: "delete-schedule", children: [_jsx("button", { onClick: () => { setShowDeleteConfirm(true); }, className: showDeleteConfirm ? 'hidden' : '', children: "Delete schedule" }), showDeleteConfirm ? (_jsx("button", { onClick: deleteSchedule, children: "Confirm delete" })) : null] })), _jsxs("div", { className: "labeled-input", children: [_jsx("label", { children: "Department" }), _jsx("div", { children: _jsx("select", { value: schedule?.department ?? department, disabled: Boolean(scheduleIdFromURL), onChange: (e) => { setDepartment(e.target.value); }, children: (schedule?.department ? [schedule.department] : departments).map(d => _jsx("option", { value: d, children: d }, d)) }) })] }), _jsxs("div", { className: "labeled-input", children: [_jsx("label", { children: "Ticket title" }), _jsx("div", { children: _jsx("input", { type: "text", placeholder: "Required", value: ticketName, onChange: (e) => { setTicketName(e.target.value); }, maxLength: 60 }) })] }), _jsx("div", { children: _jsxs("label", { children: ["Ticket description:", _jsx("textarea", { placeholder: "Required", value: ticketDescription, onChange: (e) => { setTicketDescription(e.target.value); }, maxLength: 20_000 })] }) }), _jsxs("div", { className: "labeled-input", children: [_jsx("label", { children: "First ticket date" }), _jsx("div", { children: _jsx("input", { type: "date", value: firstTicketDate, onChange: (e) => { setFirstTicketDate(e.target.value); } }) })] }), _jsxs("div", { className: "labeled-input", children: [_jsx("label", { children: "Frequency (days)" }), _jsx("div", { children: _jsx("input", { type: "number", value: frequencyInDays, onChange: (e) => { setFrequencyInDays(Number.parseInt(e.target.value)); }, min: 1 }) })] }), _jsx("div", { children: _jsx("button", { className: savable ? '' : 'hidden', onClick: saveSchedule, children: scheduleIdFromURL ? 'Save changes' : 'Create schedule' }) })] }));
};
